import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Button,
  Drawer,
  Dropdown,
  Menu,
  Popconfirm,
  Space,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';

import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';

import { AccessControl } from 'components/AccessControl';
import ClientsSelect from 'components/ClientsSelect';
import { PDFViewer } from 'components/PDFViewer';
import { RecordFields } from 'components/RecordFields';
import { DELETE_CONFIRM, FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useFilters } from 'hooks/useFilters';
import { useQueryParam } from 'hooks/useQueryParam';
import { useTribe } from 'hooks/useTribe';
import { EmployeesService } from 'services/EmployeesService';
import { ActionsWrapper } from 'ui/ActionsWrapper';
import { downloadDocs } from 'utils/files';
import {
  employeesRecordsMap,
  employeesRecordsTypes,
} from 'utils/handbooks/employeeHandbooks';
import { EmployeesRecordsForm } from './EmployeesRecordsForm';
import { EmployeesRecordsTable } from './EmployeesRecordsTable';

const PAGE_TITLE = 'Кадровые записи работника клиента';

function EmployeesRecords() {
  useDocumentTitle(PAGE_TITLE);
  const clientId = useQueryParam('client_id');
  const { setCount } = useHeaderChanger(PAGE_TITLE);
  const [employeesRecords, setEmployeesRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFormKey, setSelectedFormKey] = useState(null);
  const tribe_id = useTribe();
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();
  const {
    page,
    perPage,
    totalRows,
    setPagination,
    setTotalRows,
    resetPagination,
  } = usePagination();
  const { filters, handleFilters, resetFilters } = useFilters({
    client_id: '',
    title: null,
    full_name: null,
    iin: null,
    f_begin_date: null,
    t_begin_date: null,
    f_end_date: null,
    t_end_date: null,
    f_payment_date: null,
    t_payment_date: null,
  });
  const { client_id: selectedClient } = filters;

  const canNotPerformAction = !selectedClient;

  useEffect(() => {
    fetchEmployeesRecords();
  }, [page, perPage, filters]);

  const fetchEmployeesRecords = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await EmployeesService.getEmployeesRecords(
        page,
        perPage,
        filters,
        tribe_id
      );

      setEmployeesRecords(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const recordSrc = useMemo(() => {
    if (activeRecord) {
      return `${process.env.REACT_APP_HOSTNAME}/api/system/media/${activeRecord?.file}/`;
    }

    return '';
  }, [activeRecord]);

  const recordTitle = useMemo(() => {
    if (activeRecord && activeRecord?.file) {
      return decodeURI(
        activeRecord?.file.split('/')[activeRecord.file.split('/').length - 1]
      );
    }
    return '';
  }, [activeRecord]);

  const employeesRecordsCategoryLinks = (
    <Menu>
      {employeesRecordsTypes.map(({ key, value }) => (
        <Menu.Item
          key={key}
          onClick={() => {
            setSelectedFormKey(key);
            openCreateForm();
          }}
        >
          <Typography.Text>{value}</Typography.Text>
        </Menu.Item>
      ))}
    </Menu>
  );

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await EmployeesService.deleteEmployeeRecord(
        activeRecord.id
      );

      if (isDeleted) {
        closeDrawer();
        fetchEmployeesRecords();
        message.success('Запись успешно удалена');
      }
    } catch (err) {
      message.error('Не удалось удалить запись');
    } finally {
      hide();
    }
  };

  const recordFields = useMemo(() => {
    return [
      { label: 'ФИО сотрудника', key: 'full_name' },
      { label: 'ИИН сотрудника', key: 'iin' },
      {
        label: 'Наименование',
        key: 'record_type',
        computedValue: employeesRecordsMap[activeRecord?.record_type],
      },
      { label: 'Информация', key: 'reason' },
      { label: 'Сумма (тг)', key: 'amount' },
      { label: 'Дата начала', key: 'begin_date' },
      { label: 'Дата окончания', key: 'end_date' },
      {
        label: 'Клиент',
        key: 'company',
      },
    ];
  }, [activeRecord]);

  const handleConfirm = () => {
    handleClose();
    fetchEmployeesRecords();
    return true;
  };

  const handleClose = () => {
    setSelectedFormKey(null);
    closeForm();
  };

  const renderDrawerContent = () => {
    if (activeRecord) {
      if (activeRecord?.file) {
        return <PDFViewer file={recordSrc} widthPercent={45} />;
      }
      return <RecordFields record={activeRecord} fields={recordFields} />;
    }
    return null;
  };

  const handleDownloadPDF = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      await downloadDocs(
        recordTitle,
        `system/media/${activeRecord?.file}/`,
        'arraybuffer',
        'application/pdf'
      );
    } catch (error) {
      message.error('Не удалось скачать PDF файл');
    } finally {
      hide();
    }
  };

  const drawerActions = (
    <Space>
      {activeRecord?.file && (
        <Tooltip placement="bottomLeft" title="Скачать PDF файл">
          <Button icon={<DownloadOutlined />} onClick={handleDownloadPDF} />
        </Tooltip>
      )}

      <AccessControl pageId="record" action="update">
        <Tooltip placement="bottomLeft">
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedFormKey(activeRecord?.record_type);
              openEditForm();
            }}
          />
        </Tooltip>
      </AccessControl>

      <AccessControl pageId="record" action="delete">
        <Popconfirm
          title={DELETE_CONFIRM}
          placement="bottomLeft"
          onConfirm={handleDelete}
          okText="Да"
          cancelText="Нет"
        >
          <Tooltip placement="bottomLeft">
            <Button danger icon={<DeleteOutlined />} />
          </Tooltip>
        </Popconfirm>
      </AccessControl>
    </Space>
  );

  const handleFilterChange = (value) => {
    handleFilters(value);
    resetPagination();
  };

  const handleClientSelect = (id = '') => {
    resetFilters();
    handleFilters({ client_id: id });
    resetPagination();
  };

  return (
    <>
      <ActionsWrapper>
        <ClientsSelect
          pageTitle={PAGE_TITLE}
          clientId={clientId}
          onSelect={handleClientSelect}
          onClear={handleClientSelect}
        />

        <AccessControl pageId="record" action="create">
          <Dropdown
            disabled={canNotPerformAction}
            overlay={employeesRecordsCategoryLinks}
            trigger={['click']}
          >
            <Button type="primary" icon={<PlusOutlined />}>
              Создать
            </Button>
          </Dropdown>
        </AccessControl>
      </ActionsWrapper>

      <EmployeesRecordsTable
        allowFilter
        items={employeesRecords}
        current={page}
        pageSize={perPage}
        loading={loading}
        totalRows={totalRows}
        filters={filters}
        onRowClick={openDrawer}
        onChange={setPagination}
        onFilter={handleFilterChange}
      />

      <Drawer
        title={employeesRecordsMap[activeRecord?.record_type]}
        width="50%"
        visible={drawerVisible}
        onClose={closeDrawer}
        extra={drawerActions}
      >
        {renderDrawerContent()}
      </Drawer>

      <EmployeesRecordsForm
        isActive={formIsActive}
        initialValues={activeRecord}
        formKey={selectedFormKey}
        selectedClient={activeRecord?.company_id || selectedClient}
        onSuccess={handleConfirm}
        onClose={handleClose}
      />
    </>
  );
}

export { EmployeesRecords };
